import { sdk } from '@/lib/sdk'
import type { MediaAsset, MediaPurpose } from '@/lib/sdk-official/resources/media'
import { UploadCanceled, putToStorage } from '../courses/mediaUpload'
import { announcePlanLimit, isStorageLimitError } from '@/lib/planLimitNotice'

export { UploadCanceled, formatBytes } from '../courses/mediaUpload'

const MB = 1024 * 1024
const GB = 1024 * MB

interface MediaRule {
  kind: 'video' | 'image' | 'audio'
  maxBytes: number
  mimes: string[]
  accept: string
}

const VIDEO = { kind: 'video' as const, maxBytes: 2 * GB, mimes: ['video/mp4', 'video/webm', 'video/quicktime'], accept: '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime' }
const IMAGE = { kind: 'image' as const, maxBytes: 50 * MB, mimes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'], accept: '.jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif' }

/** Parcel photos (F-81): JPEG, PNG or WebP, no GIF, 50 MB. */
const PARCEL_PHOTO = { kind: 'image' as const, maxBytes: 50 * MB, mimes: ['image/jpeg', 'image/png', 'image/webp'], accept: '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp' }

/** F-178: an episode's audio, never WAV (Apple Podcasts refuses it in the feed). */
const EPISODE_AUDIO = { kind: 'audio' as const, maxBytes: 500 * MB, mimes: ['audio/mpeg', 'audio/mp3', 'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/aac', 'audio/x-aac'], accept: '.mp3,.m4a,.aac,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac' }

/** F-107: a Drop on a short post or a comment, 250 MB (10 minutes at ~2.5 Mbps is ~190 MB). */
const DROP = { ...VIDEO, maxBytes: 250 * MB }

/** Mirrors the API's rules for each purpose; the server checks again before signing and on confirm. */
export const MEDIA_RULES: Record<MediaPurpose, MediaRule> = {
  contentVideo: VIDEO,
  eventRecording: VIDEO,
  galleryImage: IMAGE,
  classifiedImage: IMAGE,
  parcelPhoto: PARCEL_PHOTO,
  supportPhoto: PARCEL_PHOTO,
  statusVideo: DROP,
  contentCommentVideo: DROP,
  statusCommentVideo: DROP,
  /** A lesson video recorded as a Drop (owner/admin, the course's space). */
  lessonVideo: DROP,
  /** F-107: a video's chosen cover (JPEG, PNG or WebP). */
  videoPoster: PARCEL_PHOTO,
  /** F-139: a topic photo on an Upvote board (JPEG, PNG or WebP, no GIF). */
  upvoteImage: PARCEL_PHOTO,
  /** F-138: a photo in a channel message (GIF included, 50 MB). */
  channelImage: IMAGE,
  /** F-138: a video in a channel message, recorded or sent (10 min, 350 MB, like the API). */
  channelVideo: { ...VIDEO, maxBytes: 350 * MB },
  /** F-155: the welcome video, up to 2 GB like a post's (no space). */
  welcomeVideo: VIDEO,
  /** F-178 Podcast and Vídeos. */
  episodeAudio: EPISODE_AUDIO,
  episodeVideo: { ...VIDEO, maxBytes: 4 * GB },
  episodeCover: PARCEL_PHOTO,
  showCover: PARCEL_PHOTO,
  mediaEpisodeCommentVideo: DROP,
  /** F-178 1b: "Podcast" and "Vídeo" of the Feed's "Novo Status", the episodes' rules. */
  feedAudio: EPISODE_AUDIO,
  feedVideo: { ...VIDEO, maxBytes: 4 * GB },
}

/** `null` when the file may be sent; otherwise which rule it breaks. */
export const checkMediaFile = (purpose: MediaPurpose, file: File): 'tooBig' | 'type' | null => {
  const rule = MEDIA_RULES[purpose]
  if (file.size > rule.maxBytes) return 'tooBig'
  return rule.mimes.includes(file.type) ? null : 'type'
}

export interface UploadedMedia {
  asset: MediaAsset
  warning: 'nearLimit' | 'overLimit' | null
}

/**
 * upload-url, then the direct PUT with progress, then confirm. Resolves with the confirmed asset;
 * its `id` goes in the owner's body (`videoMediaId`, `mediaId`, `mediaIds`). Nothing is attached yet.
 */
export const uploadMedia = async (opts: {
  purpose: MediaPurpose
  /** Every purpose but `welcomeVideo` (F-155) needs it. */
  spaceId?: string
  file: File
  onProgress?: (percent: number) => void
  signal?: AbortSignal
}): Promise<UploadedMedia> => {
  const { purpose, spaceId, file, onProgress, signal } = opts
  let ticket: Awaited<ReturnType<typeof sdk.media.requestUpload>>
  try {
    ticket = await sdk.media.requestUpload({ purpose, ...(spaceId ? { spaceId } : {}), name: file.name, mimeType: file.type, size: file.size })
  } catch (error) {
    /* F-34 `media.storageGb`: the form shows the refusal; the toast offers the plan change. */
    if (isStorageLimitError(error)) announcePlanLimit({ kind: 'storage' })
    throw error
  }
  if (signal?.aborted) throw new UploadCanceled()
  await putToStorage(ticket, file, onProgress ?? (() => undefined), signal ?? new AbortController().signal)
  const confirmed = await sdk.media.confirm({ key: ticket.key, name: ticket.name || file.name, ...(spaceId ? { spaceId } : {}), size: file.size })
  /* F-34: past the plan's conversion minutes the video is kept and plays its original file. */
  if (confirmed.transcodeLimit) announcePlanLimit({ kind: 'transcode', code: confirmed.transcodeLimit.code, message: confirmed.transcodeLimit.message })
  return { asset: confirmed.asset, warning: confirmed.warning ?? null }
}

/**
 * Media uploaded in this tab that no item has saved yet. Shared by every slot (file upload and
 * Drops), so replacing or removing a video from either slot discards what the other one uploaded.
 * Deleting is safe even if the item was saved meanwhile: the API answers 409 for attached media.
 */
const unsavedUploads = new Set<string>()

export const rememberUpload = (id: string) => { unsavedUploads.add(id) }

/** Deletes `id` when this tab uploaded it and it may still be unattached; otherwise does nothing. */
export const discardUpload = (id: string | null | undefined) => {
  if (!id || !unsavedUploads.has(id)) return
  unsavedUploads.delete(id)
  void sdk.media.delete(id).catch(() => undefined)
}
